import { RouterContext } from '@core/route-context/RouteContext';
import { toJSON } from '@utils';
import { useContext, useMemo, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
/**
 * @export
 * Shared utility function to parse URLSearchParams into a typed object.
 * Handles JSON-encoded values using the toJSON utility.
 *
 * @param params - The URLSearchParams object to parse.
 * @returns A typed object containing the parsed search parameters.
 *
 * @example
 * ```
 * const params = new URLSearchParams('?name=John&age=30');
 * const result = parseSearchParams<{ name: string; age: string }>(params);
 * // result: { name: 'John', age: '30' }
 * ```
 *
 * @example
 * ```
 * const params = new URLSearchParams('?user={"name":"John","age":30}');
 * const result = parseSearchParams<{ user: { name: string; age: number } }>(params);
 * // result: { user: { name: 'John', age: 30 } }
 * ```
 */
function parseSearchParams(params) {
    const result = {};
    for (const [key, value] of params.entries()) {
        if (value.startsWith('{') || value.startsWith('[')) {
            result[key] = toJSON(value, null) || value;
        }
        else {
            result[key] = value;
        }
    }
    return result;
}
/**
 * @export
 * A custom React hook that extracts search parameters from the URL using react-router-dom.
 * Handles JSON-encoded values using the toJSON utility. Must be used within a react-router-dom context.
 *
 * @returns A typed object containing the parsed search parameters.
 *
 * @example
 * ```
 * const MyComponent = () => {
 *   const params = useSearchParams<{ name: string; age: string }>();
 *   return <div>Name: {params.name}, Age: {params.age}</div>;
 * };
 *
 * ```
 *
 * @example
 * ```
 * const MyComponent = () => {
 *   const params = useSearchParams<{ user: { name: string; age: number } }>();
 *   return (
 *     <div>
 *       User: {params.user?.name}, Age: {params.user?.age}
 *     </div>
 *   );
 * };
 *
 * ```
 */
export function useSearchParams() {
    const { search } = useLocation();
    const stableRef = useRef({});
    return useMemo(() => {
        const params = new URLSearchParams(search);
        const parsed = parseSearchParams(params);
        const prev = stableRef.current;
        const prevKeys = Object.keys(prev);
        const currKeys = Object.keys(parsed);
        const hasChanged = prevKeys.length !== currKeys.length ||
            currKeys.some(k => prev[k] !== parsed[k]);
        if (!hasChanged)
            return prev;
        stableRef.current = parsed;
        return parsed;
    }, [search]);
}
/**
 * @export
 * Extracts search parameters from a URL or search string into a typed object.
 * Handles JSON-encoded values using the toJSON utility.
 *
 * @param urlOrSearch - The full URL or just the search string (e.g., "?key=value" or "http://example.com?key=value").
 * @returns A typed object containing the parsed search parameters.
 *
 * @example
 * ```
 * const params = getSearchParamsFromURL<{ name: string; age: string }>('?name=John&age=30');
 * // params: { name: 'John', age: '30' }
 * ```
 *
 * @example
 * ```
 * const params = getSearchParamsFromURL<{ user: { name: string; age: number } }>(
 *   'http://example.com?user={"name":"John","age":30}'
 * );
 * // params: { user: { name: 'John', age: 30 } }
 * ```
 */
export function getSearchParamsFromURL(urlOrSearch) {
    // Extract search string if a full URL is provided
    const search = urlOrSearch.includes('?') ? new URL(urlOrSearch).search : urlOrSearch;
    const params = new URLSearchParams(search);
    return parseSearchParams(params);
}
/**
 * @export
 * A custom React hook that wraps react-router-dom's useNavigate for typed navigation.
 * Must be used within a react-router-dom context.
 *
 * @returns A CustomNavigateFunction for programmatic navigation.
 *
 * @example
 * ```
 * const MyComponent = () => {
 *   const navigate = useNavigateWrapper();
 *   return <button onClick={() => navigate('/home')}>Go to Home</button>;
 * };
 *
 * ```
 *
 * @example
 * ```
 * const MyComponent = () => {
 *   const navigate = useNavigateWrapper();
 *   return (
 *     <div>
 *       <button onClick={() => navigate('/profile', { state: { userId: 123 } })}>
 *         Go to Profile
 *       </button>
 *       <button onClick={() => navigate(-1)}>Go Back</button>
 *     </div>
 *   );
 * };
 *
 * ```
 */
export function useNavigateWrapper() {
    const navigate = useNavigate();
    return navigate;
}
/**
 * @export
 * A custom React hook that retrieves the RouterContext, throwing an error if used outside a RouterContextProvider.
 *
 * @returns The RouterContext object.
 * @throws Error if the hook is used outside a RouterContextProvider.
 *
 * @example
 * ```
 * const MyComponent = () => {
 *   const routerContext = useRouterContext();
 *   return <div>{routerContext.someProperty}</div>;
 * };
 * ```
 *
 * @example
 * ```
 * const MyComponent = () => {
 *   const { someProperty, navigate } = useRouterContext();
 *   return (
 *     <div>
 *       <p>{someProperty}</p>
 *       <button onClick={() => navigate('/dashboard')}>
 *         Go to Dashboard
 *       </button>
 *     </div>
 *   );
 * };
 *
 *
 * ```
 */
export const useRouterContext = () => {
    const context = useContext(RouterContext);
    if (!context) {
        throw new Error('useRouterContext must be used within RouterContextProvider');
    }
    return context;
};
